import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatMoney, toCents } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Lock } from "lucide-react";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

export default function CloseCashDialog({ open, onOpenChange, session, currency }) {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [actual, setActual] = useState("");
  const [note, setNote] = useState("");
  const t = session?.totals || {};
  const expected = t.expected_cash_cents || 0;
  const actualCents = actual === "" ? null : toCents(actual);
  const diff = actualCents === null ? null : actualCents - expected;
  const needNote = diff !== null && diff !== 0;

  const mut = useMutation({
    mutationFn: async () => (await api.post(`/api/cash/${session.id}/close`,
      { actual_closing_cash_cents: actualCents, closing_note: note || null }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Caisse fermée"); qc.invalidateQueries(); onOpenChange(false); setActual(""); setNote(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  const m = (c) => formatMoney(c, currency);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="close-cash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Fermer la caisse</DialogTitle>
          <DialogDescription className="sr-only">Comptez la caisse physique et confirmez la fermeture avec l'écart éventuel.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5 rounded-lg bg-slate-50 p-3 text-sm">
            <Row k="Fond de caisse" v={m(t.opening_cash_cents)} />
            <Row k="Encaissements espèces" v={m(t.cash_payments_cents)} />
            <Row k="Dépenses espèces" v={`-${m(t.cash_expenses_cents)}`} />
            <Row k="Dépôts" v={m(t.deposits_cents)} />
            <Row k="Retraits" v={`-${m(t.withdrawals_cents)}`} />
            <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900">
              <span>Caisse théorique</span><span data-testid="close-expected-cash">{m(expected)}</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Montant réellement compté ({currency})</label>
            <input type="number" min="0" value={actual} onChange={(e) => setActual(e.target.value)} placeholder="5650" data-testid="actual-cash-input" className={input} autoFocus />
          </div>
          {diff !== null && (
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${diff === 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="close-difference">
              <span>Écart de caisse</span>
              <span>{diff > 0 ? "+" : ""}{m(diff)}</span>
            </div>
          )}
          {needNote && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Motif de l'écart (obligatoire)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Erreur de rendu monnaie" data-testid="closing-note-input" className={input} />
            </div>
          )}
          <button disabled={mut.isPending || actual === "" || (needNote && !note.trim())} onClick={() => mut.mutate()} data-testid="confirm-close-cash-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer la fermeture
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>
);
